import React, { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import * as S from '../../css/CalendarComponent.styles';
import { useRecoilValue, useSetRecoilState } from "recoil";
import { loginState } from '../../stores/loginState';
import { formatNumberCommas } from '../../utils/formatNumberCommas';
import useGetBarcodeData from '../../api/barcode/useGetBarcodeData';
import { useNavigate } from 'react-router-dom';
import { calendarDataState } from '../../stores/calendar/calendarDataState';
import { todayState } from '../../stores/dashboard/todayState';
import dayjs from 'dayjs';
import { isBarcodeLoading } from '../../stores/barcode/isBarcodeLoading';

function CalendarComponent({ date, setDate, viewMonth, setViewMonth }) {
  // NOTE 상태 변수 선언
  const login = useRecoilValue(loginState); // 로그인 전역변수
  const calendarData = useRecoilValue(calendarDataState); // 월간 데이터 
  const [selectedDate, setSelectedDate] = useState(null); // 선택된 날짜 (당장은 사용안함)
  const calendarRef = useRef(null); // 오늘 날짜에 맞춰 화면이동
  const [apiDate, setApiDate] = useState(null); // API 호출용 날짜변수
  const navigate = useNavigate(); // 페이지 이동 함수
  const todayData = useRecoilValue(todayState); // 오늘자 데이터
  const setIsLoading = useSetRecoilState(isBarcodeLoading); // 로딩 상태 추가
  
  // TODO 선택된 일자 조회 API
  const { mutateAsync: getBarcodeData } = useGetBarcodeData({
    startDate: apiDate,
    endDate: apiDate,
    workType: "전체",
    barcode: "",
  });

  // NOTE 날짜 변경 시
  const onChange = newDate => {
    setDate(newDate);
  };

  // NOTE 월이 변경되었을 때 호출
  const onActiveStartDateChange = ({ activeStartDate }) => {
    setViewMonth(activeStartDate.getMonth()); // 현재 보이는 월 설정
  };

  // NOTE 현재 보이는 월과 다르면 비활성화
  const tileDisabled = ({ date, view }) => {
    return (
      view === 'month' &&
      date.getMonth() !== viewMonth 
    );
  };

  // NOTE 특정 날짜에 대해 클래스명을 부여하는 함수
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const classes = [];
      
      // 주말 처리
      if (date.getDay() === 0) {
        classes.push('react-calendar__tile--sunday'); // 일요일
      }
      if (date.getDay() === 6) {
        classes.push('react-calendar__tile--saturday'); // 토요일
      }
      
      // 현재 월이 아닌 날짜 처리
      if (date.getMonth() !== viewMonth) {
        classes.push('react-calendar__tile--not-current-month'); // 현재 월이 아닌 날짜
      }
      
      return classes.join(' '); // 여러 클래스 이름을 공백으로 구분하여 반환
    }
  };  

  // NOTE 특정 날짜 클릭 시
  const onClickDay = (value) => {
    if (selectedDate && value.getTime() === selectedDate.getTime()) {
      setSelectedDate(null);
    } else {
      setSelectedDate(value);
    }
  };

  // NOTE 해당 날짜 바코드 조회 시
  const searchBarcode = async (value) => {
    setIsLoading(true);
    const formattedDate = new Date(value.getTime() - (value.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
    await setApiDate(formattedDate);

    navigate("/Barcode");
    await getBarcodeData();
    setIsLoading(false);
  };

  // NOTE 날짜별 운송장 건수 구하기 (사실상 반복문임)
  const renderTileContent = ({ date, view }) => {
    if (view === 'month' && calendarData) {
      const formattedDate = dayjs(date).format('YYYY-MM-DD'); // date 날짜 형식 YYYY-MM-DD로 변경
      const today = dayjs().format('YYYY-MM-DD'); // 오늘 날짜 형식 YYYY-MM-DD로 설정
      const isToday = formattedDate === today; // 타일의 날짜가 오늘의 날짜인지 확인
      const isPastDate = formattedDate <= today; // 오늘 이전의 날짜인지 확인
      
      // dayData.dayskey 안에 이달 데이터 다 들어있음
      const dayData = calendarData.find((monthData) => monthData.dayskey.hasOwnProperty(formattedDate));
      const entry = dayData ? dayData.dayskey[formattedDate] : null; // dayskey 안에 있는 오늘 데이터 가져오기

      // 오늘이라면 todayData에서 가져오고, 아니라면 기존 calendarData에서 가져와서 설정
      const deliveryInCount = isToday ? todayData.deliveryInTotal : (entry ? parseInt(entry.deliveryInCount) : 0) || 0; // 해당 날짜 배송입고
      const deliveryOutCount = isToday ? todayData.deliveryOutTotal : (entry ? parseInt(entry.deliveryOutCount) : 0) || 0; // 해당 날짜 집하출고
      const total = isToday ? todayData.total : (deliveryInCount + deliveryOutCount); // 해당 날짜 도합
      
      if (dayData || isPastDate) { // dayData가 있거나 오늘, 이전 날짜인 경우
        return (
          <div className="memo">
            <S.SmallSpan>
              <p>{login.company === "LOGEN" ? "배송입고" : login.company === "LOTTE" ? "도　　착" : "간선하차"} : </p><h6>{formatNumberCommas(deliveryInCount)} 개</h6>
            </S.SmallSpan>
            <S.SmallSpan>
              <p>{login.company === "LOGEN" ? "집하출고" : login.company === "LOTTE" ? "발　　송" : "간선상차"} : </p><h6>{formatNumberCommas(deliveryOutCount)} 개</h6>
            </S.SmallSpan>
            <S.SmallSpan className="search" onClick={() => searchBarcode(date)}>
              <p>합　　계 : </p><h6>{formatNumberCommas(total)} 개</h6>
            </S.SmallSpan>
          </div>
        );
      }
    }
    return null;
  };

  // NOTE 페이지 로딩 시
  useEffect(() => {
    if (calendarRef.current) {
      const todayTile = calendarRef.current.querySelector('.react-calendar__tile--now'); // 오늘 날짜로 이동
      if (todayTile) {
        todayTile.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  return (
    <S.CalendarContainer ref={calendarRef}>
      <Calendar 
        onChange={onChange} 
        value={date} 
        locale="ko" 
        calendarType="gregory"
        tileContent={renderTileContent}
        tileDisabled={tileDisabled}
        tileClassName={tileClassName}
        onClickDay={onClickDay}
        onActiveStartDateChange={onActiveStartDateChange} // 월 변경 시 호출되는 함수
        activeStartDate={new Date(date.getFullYear(), viewMonth, 1)} // 현재 보이는 월의 시작일로 설정
      />
    </S.CalendarContainer>
  );
}

export default CalendarComponent;
